import React, { useState } from 'react';
import { X, Download, FileText, Image as ImageIcon, ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react';
import { CrlvFile } from '../../types/crlv';

interface CrlvViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  crlvFile: CrlvFile | null;
}

export function CrlvViewerModal({ isOpen, onClose, crlvFile }: CrlvViewerModalProps) {
  const [imgError, setImgError] = useState(false);

  if (!isOpen || !crlvFile) return null;

  const isLocalFileUrl = crlvFile.relativePath.toLowerCase().startsWith('file:');
  const isImage = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(crlvFile.extension.toLowerCase());
  const isPdf = crlvFile.extension.toLowerCase() === 'pdf';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 p-2 sm:p-4 backdrop-blur-xs">
      <div className="flex h-[92vh] sm:h-[90vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 bg-slate-50 gap-3">
          <div className="flex items-center gap-3 overflow-hidden">
            {isImage ? (
              <ImageIcon className="h-6 w-6 text-blue-600 shrink-0" />
            ) : (
              <FileText className="h-6 w-6 text-red-600 shrink-0" />
            )}
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 text-base sm:text-lg leading-tight truncate">{crlvFile.name}</h3>
              <p className="text-[11px] sm:text-xs text-slate-500 font-mono truncate">{crlvFile.relativePath}</p>
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-2 shrink-0">
            {!isLocalFileUrl && (
              <>
                <a
                  href={crlvFile.relativePath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors shadow-2xs"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Abrir em</span> Nova Aba
                </a>
                <a
                  href={crlvFile.relativePath}
                  download={crlvFile.name}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors shadow-2xs"
                >
                  <Download className="h-3.5 w-3.5" />
                  Baixar
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="ml-auto sm:ml-2 rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors cursor-pointer"
              title="Fechar visualizador"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Viewer Body */}
        <div className="flex-1 overflow-auto bg-slate-100 p-2 sm:p-4 flex items-center justify-center">
          {isLocalFileUrl ? (
            <div className="text-center p-6 sm:p-8 bg-white rounded-xl shadow-md border border-amber-200 max-w-lg mx-auto">
              <div className="h-12 w-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h4 className="font-bold text-slate-800 text-base mb-2">Caminho de Arquivo Local Detectado</h4>
              <p className="text-xs sm:text-sm text-slate-600 mb-4 leading-relaxed">
                Este veículo está configurado com um endereço do seu computador local:
                <br />
                <code className="bg-slate-100 p-1 rounded text-[11px] font-mono break-all text-slate-700 block mt-1">
                  {crlvFile.relativePath}
                </code>
              </p>
              <p className="text-xs text-slate-500 mb-4">
                Navegadores não permitem que páginas web acessem arquivos do seu disco rígido diretamente por segurança. Para visualizar este documento em qualquer computador ou celular, use o botão <strong>Enviar CRLV</strong> no cadastro do veículo.
              </p>
              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
              >
                Entendido
              </button>
            </div>
          ) : isImage ? (
            !imgError ? (
              <img
                src={crlvFile.relativePath}
                alt={crlvFile.name}
                onError={() => setImgError(true)}
                className="max-h-full max-w-full object-contain rounded-lg shadow-md border border-slate-200 bg-white"
              />
            ) : (
              <div className="text-center p-6 sm:p-8 bg-white rounded-xl shadow border border-slate-200 max-w-md">
                <ImageIcon className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                <h4 className="font-semibold text-slate-800 text-base mb-2">Não foi possível carregar a imagem</h4>
                <p className="text-xs sm:text-sm text-slate-500 mb-4">
                  O arquivo pode estar com nome especial ou requerer download direto.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <a
                    href={crlvFile.relativePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Tentar Abrir Link
                  </a>
                  <button
                    type="button"
                    onClick={() => setImgError(false)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Tentar Novamente
                  </button>
                </div>
              </div>
            )
          ) : isPdf ? (
            <div className="w-full h-full flex flex-col">
              <object
                data={crlvFile.relativePath}
                type="application/pdf"
                className="w-full h-full rounded-lg border border-slate-300 bg-white shadow-inner flex-1"
              >
                <iframe
                  src={crlvFile.relativePath}
                  title={crlvFile.name}
                  className="w-full h-full rounded-lg border border-slate-300 bg-white shadow-inner"
                >
                  <div className="p-6 text-center bg-white rounded-lg">
                    <FileText className="h-12 w-12 text-red-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-800 mb-2">
                      Pré-visualização direta de PDF não suportada neste dispositivo/navegador.
                    </p>
                    <a
                      href={crlvFile.relativePath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      <Download className="h-4 w-4" />
                      Baixar ou Abrir PDF
                    </a>
                  </div>
                </iframe>
              </object>
            </div>
          ) : (
            <div className="text-center p-8 bg-white rounded-xl shadow border border-slate-200 max-w-md">
              <FileText className="h-16 w-16 text-slate-400 mx-auto mb-4" />
              <h4 className="font-semibold text-slate-800 text-base mb-2">Pré-visualização direta indisponível</h4>
              <p className="text-sm text-slate-500 mb-6">
                Este tipo de arquivo (.{crlvFile.extension}) pode ser baixado ou aberto externamente.
              </p>
              <a
                href={crlvFile.relativePath}
                download={crlvFile.name}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                <Download className="h-4 w-4" />
                Baixar Arquivo ({crlvFile.extension.toUpperCase()})
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
